"use client";

import Link from "next/link";
import React from "react";
import { INewCategory } from "../../types";
import { StyledCategoryPills } from "./categoryPills.styles";

interface ICategoryPillsProps {
  categories: INewCategory[];
  activeSlug?: string;
  maxVisible?: number;
}

export const slugify = (name: string) =>
  (name || "").toLowerCase().replaceAll(" ", "-");

const PRIMARY_CATEGORY_NAMES = [
  "How To",
  "Widgets",
  "Web Development",
  "Web Design",
  "Website Building",
  "Marketing",
  "Business",
  "News & Updates",
  "Interviews & Webinars",
];

export const CategoryPills = ({
  categories,
  activeSlug,
  maxVisible = 9,
}: ICategoryPillsProps) => {
  if (!categories?.length) return null;

  const byName = new Map(categories.map((c) => [c.name.toLowerCase(), c]));
  const primary = PRIMARY_CATEGORY_NAMES
    .map((n) => byName.get(n.toLowerCase()))
    .filter((c): c is INewCategory => Boolean(c));

  // De-duplicate (e.g. "How-To" and "How To" alias)
  const seen = new Set<string>();
  const dedupedPrimary = primary.filter((c) => {
    const k = String(c._id || c.name);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const visible = dedupedPrimary.slice(0, maxVisible);

  // Always include the active category, even if it's outside the primary list.
  if (activeSlug) {
    const activeCat = categories.find((c) => slugify(c.name) === activeSlug);
    if (activeCat && !visible.find((c) => (c._id || c.name) === (activeCat._id || activeCat.name))) {
      visible.push(activeCat);
    }
  }

  return (
    <StyledCategoryPills aria-label="Blog categories">
      <div className="pills-track">
        <Link
          href="/blog"
          className={`pill ${!activeSlug ? "active" : ""}`}
          title="All posts"
        >
          All
        </Link>
        {visible.map((cat) => {
          const slug = slugify(cat.name);
          return (
            <Link
              key={cat._id || cat.name}
              href={`/blog/category/${slug}`}
              className={`pill ${activeSlug === slug ? "active" : ""}`}
              title={cat.name}
            >
              {cat.name}
            </Link>
          );
        })}
      </div>
    </StyledCategoryPills>
  );
};
